import { z } from 'zod'
import { positionSchema, resolvedAddressSchema } from './schema'
import { safeStorage } from './utils'

const KEY = 'qwickgo-checkout-v1'

const cartItemSchema = z.object({
  skillName: z.string(),
  quantity: z.number(),
  hours: z.number(),
})

// Prefill only and may be partial; must round-trip empty/invalid fields.
const persistedFormSchema = z.object({
  firstName: z.string().catch(''),
  lastName: z.string().catch(''),
  email: z.string().catch(''),
  phone: z.string().catch(''),
  company: z.string().catch(''),
  address: z.string().catch(''),
  resolvedAddress: resolvedAddressSchema.nullable().catch(null),
  date: z.string().catch(''),
  positions: z.array(positionSchema).catch([]),
})

const persistedCheckoutSchema = z.object({
  session: z.object({ id: z.string(), token: z.string() }).nullable(),
  cartItems: z.array(cartItemSchema),
  formValues: persistedFormSchema,
  step: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  clientSecret: z.string().nullable(),
  paymentAuthorized: z.boolean(),
  paymentLast4: z.string().nullable(),
  shiftUnlocked: z.boolean(),
})

export type PersistedCheckout = z.infer<typeof persistedCheckoutSchema>

const isBrowser = () => typeof window !== 'undefined'

export const savePersisted = (state: PersistedCheckout): void => {
  if (!isBrowser()) return
  safeStorage(() => sessionStorage.setItem(KEY, JSON.stringify(state)))
}

export const loadPersisted = (): PersistedCheckout | null => {
  if (!isBrowser()) return null
  const raw = safeStorage(() => sessionStorage.getItem(KEY))
  if (!raw) return null
  const parsed = safeStorage(() => JSON.parse(raw) as unknown)
  if (parsed === undefined) return null
  const result = persistedCheckoutSchema.safeParse(parsed)
  return result.success ? result.data : null
}

export const clearPersisted = (): void => {
  if (!isBrowser()) return
  safeStorage(() => sessionStorage.removeItem(KEY))
}

const STRIPE_RETURN_PARAMS = [
  'payment_intent',
  'payment_intent_client_secret',
  'redirect_status',
] as const

export type StripeReturn = {
  paymentIntent: string | null
  redirectStatus: string | null
}

export const detectStripeReturn = (): StripeReturn | null => {
  if (!isBrowser()) return null
  const p = new URLSearchParams(window.location.search)
  const redirectStatus = p.get('redirect_status')
  if (redirectStatus === null && !p.has('payment_intent_client_secret')) {
    return null
  }
  return { paymentIntent: p.get('payment_intent'), redirectStatus }
}

export const stripStripeReturnParams = (): void => {
  if (!isBrowser()) return
  const url = new URL(window.location.href)
  const before = url.searchParams.size
  STRIPE_RETURN_PARAMS.forEach((k) => url.searchParams.delete(k))
  if (url.searchParams.size !== before) {
    window.history.replaceState({}, '', url.toString())
  }
}
